import { useEffect, useState } from "react";
import {
  Page,
  Card,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Modal,
  TextField,
  Link,
  Collapsible,
  Divider,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatFriendlyDate } from "../../utility/funcs";
import { PorFacturar } from "./GastosFacturas";
import { KIND_LABEL } from "./TrasladoTicketsCard";
import SpeiPaymentModal from "../Services/SpeiPaymentModal";
import { speiEnabled } from "../../utility/pagos";
import { loadToSettle } from "./settlementPreview";

// Everything across traslados that is waiting on staff, in the order it gets
// done: invoice the portal tickets, approve or reject the tickets the robot
// flagged, then settle and pay what each traslado owes. Each card hides itself when empty.

const thumb = { width: 56, height: 56, objectFit: "cover", borderRadius: 6, background: "#eee" };
const unit = (o) => [o?.car?.plate, o?.car?.model?.name].filter(Boolean).join(" · ");

async function signedUrls(paths) {
  if (!paths.length) return {};
  const { data } = await supabase.storage.from("traslado-tickets").createSignedUrls(paths, 3600);
  return Object.fromEntries((data || []).map((x) => [x.path, x.signedUrl]));
}

// ------------------------------------------------------------ Por aprobar
function PorAprobar({ canReview, onCount, onChange }) {
  const [rows, setRows] = useState([]);
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [noCfdi, setNoCfdi] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const { data, error: err } = await supabase
      .from("traslado_tickets")
      .select(
        "*, trasladista:trasladistas(name), order:traslado_orders(id, car(plate, model(name))), cfdis:expense_cfdis(uuid, emisor_nombre, emisor_rfc, total)",
      )
      .eq("status", "needs_review")
      .order("created_at", { ascending: false });
    if (err) setError(err.message);
    setRows(data || []);
    onCount(err ? -1 : (data || []).length);
    setUrls(await signedUrls((data || []).map((t) => t.photo_path)));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn, args) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc(fn, args);
    setBusy(false);
    if (err) {
      setError(err.message);
      return false;
    }
    setRejecting(null);
    setNoCfdi(null);
    setReason("");
    load();
    onChange();
    return true;
  };

  return (
    <BlockStack gap="300">
      {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
      {rows.map((t) => {
        const cfdi = (t.cfdis || []).find((c) => c.uuid === t.cfdi_uuid);
        return (
          <InlineStack key={t.id} gap="300" blockAlign="center" wrap={false}>
            <a href={urls[t.photo_path]} target="_blank" rel="noreferrer">
              <img src={urls[t.photo_path]} alt="ticket" style={thumb} />
            </a>
            <BlockStack gap="050">
              <InlineStack gap="200" blockAlign="center">
                <Link url={`/vehicle_transfers/${t.traslado_order}`}>{`Traslado #${t.traslado_order}`}</Link>
                <Text tone="subdued" variant="bodySm">{[unit(t.order), t.trasladista?.name].filter(Boolean).join(" · ")}</Text>
                {t.duplicate_of && <Badge tone="critical">Posible duplicado</Badge>}
              </InlineStack>
              <Text variant="bodySm">
                {[KIND_LABEL[t.kind] || t.kind, t.merchant_name, t.ticket_date, t.liters ? `${t.liters} L` : null].filter(Boolean).join(" · ")}
              </Text>
              {cfdi && (
                <Text tone="subdued" variant="bodySm">
                  {`Factura ${cfdi.emisor_nombre || cfdi.emisor_rfc} · ${formatPrice(cfdi.total)}`}
                </Text>
              )}
              {t.review_reason && <Text tone="caution" variant="bodySm">{t.review_reason}</Text>}
            </BlockStack>
            <div style={{ marginLeft: "auto" }}>
              <BlockStack gap="100" inlineAlign="end">
                <Text fontWeight="semibold">{t.total != null ? formatPrice(t.total) : "—"}</Text>
                {canReview && (
                  <InlineStack gap="100" wrap={false}>
                    <Button
                      size="slim"
                      loading={busy}
                      onClick={() => run("decide_traslado_ticket", { p_ticket: t.id, p_approve: true, p_reason: null })}
                    >
                      Aprobar
                    </Button>
                    <Button size="slim" tone="critical" variant="plain" onClick={() => setRejecting(t)}>
                      Rechazar
                    </Button>
                  </InlineStack>
                )}
                {canReview && !t.cfdi_uuid && (
                  <Button size="slim" variant="plain" onClick={() => setNoCfdi(t)}>
                    Aprobar sin factura
                  </Button>
                )}
              </BlockStack>
            </div>
          </InlineStack>
        );
      })}

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Rechazar gasto"
        primaryAction={{
          content: "Rechazar",
          destructive: true,
          loading: busy,
          disabled: reason.trim().length < 5,
          onAction: () => run("decide_traslado_ticket", { p_ticket: rejecting.id, p_approve: false, p_reason: reason }),
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setRejecting(null) }]}
      >
        <Modal.Section>
          <TextField label="Motivo (lo verá el trasladista)" autoComplete="off" value={reason} onChange={setReason} />
        </Modal.Section>
      </Modal>
      <Modal
        open={!!noCfdi}
        onClose={() => setNoCfdi(null)}
        title={noCfdi ? `Aprobar sin factura ${formatPrice(noCfdi.total)}` : ""}
        primaryAction={{
          content: "Aprobar como no deducible",
          loading: busy,
          disabled: reason.trim().length < 5,
          onAction: () => run("approve_ticket_without_cfdi", { p_ticket: noCfdi.id, p_reason: reason }),
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setNoCfdi(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text tone="subdued">
              Se reembolsa al trasladista en la siguiente liquidación, pero queda como gasto no deducible.
            </Text>
            <TextField
              label="¿Por qué se reembolsa sin factura?"
              autoComplete="off"
              value={reason}
              onChange={setReason}
              placeholder="Ej. Puesto de carretera que no factura"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}

// ------------------------------------------- Facturas recibidas sin ticket
// CFDIs that arrived at gastos@ (or were uploaded) that the robot couldn't
// match to a single ticket, but that some ticket could still take. gastos@
// gets every invoice, so ones no ticket could take stay out of here (they're
// under "Sin ticket" in Facturas de gastos). Assigning happens there.
function FacturasPorRevisar({ onCount }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    supabase
      .from("expense_cfdis")
      .select("id, emisor_nombre, emisor_rfc, total, fecha, from_email, source, notes")
      .eq("status", "unmatched")
      .eq("has_ticket_candidate", true)
      .order("created_at", { ascending: false })
      .then(({ data, error: err }) => {
        if (err) setError(err.message);
        setRows(data || []);
        onCount(err ? -1 : (data || []).length);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <BlockStack gap="200">
      {error && <Banner tone="critical">{error}</Banner>}
      {rows.map((r, i) => (
        <BlockStack key={r.id} gap="200">
          {i > 0 && <Divider />}
          <InlineStack align="space-between" blockAlign="center" wrap={false}>
            <BlockStack gap="0">
              <Text fontWeight="semibold">{r.emisor_nombre || r.emisor_rfc}</Text>
              <Text tone="subdued" variant="bodySm">
                {[r.emisor_rfc, r.fecha && formatFriendlyDate(r.fecha), r.source === "upload" ? "subida a mano" : r.from_email, r.notes]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            </BlockStack>
            <Text fontWeight="semibold">{formatPrice(r.total)}</Text>
          </InlineStack>
        </BlockStack>
      ))}
      <InlineStack>
        <Button url="/gastos_facturas?tab=unmatched">Asignar o rechazar en Facturas de gastos</Button>
      </InlineStack>
    </BlockStack>
  );
}

// ---------------------------------------------------- Liquidaciones
// "Por liquidar": what traslados owe that no settlement covers yet (unpaid
// anticipo, approved tickets) → Liquidar creates the settlement. "Por pagar":
// settlements created and waiting for the SPEI.
function LiquidacionesPendientes({ canPay, onCount, reloadKey }) {
  const [rows, setRows] = useState([]);
  const [owed, setOwed] = useState([]);
  const [urls, setUrls] = useState({});
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(null);
  const [speiFor, setSpeiFor] = useState(null);
  const [ref, setRef] = useState("");

  const load = async () => {
    const [{ data, error: err }, toSettle] = await Promise.all([
      supabase
        .from("traslado_settlements")
        .select(
          "*, trasladista:trasladistas(name), order:traslado_orders(id, origin_label, dest_label, car(plate, model(name))), tickets:traslado_tickets!traslado_tickets_settlement_fkey(id, kind, merchant_name, ticket_date, total, deductible, photo_path)",
        )
        .eq("status", "pending_payment")
        .order("created_at", { ascending: true }),
      loadToSettle(),
    ]);
    const failed = err || toSettle.error;
    if (failed) setError(failed.message);
    setRows(data || []);
    setOwed(toSettle.rows || []);
    onCount(failed ? -1 : (data || []).length + (toSettle.rows || []).length);
    setUrls(
      await signedUrls([
        ...(data || []).flatMap((x) => (x.tickets || []).map((t) => t.photo_path)),
        ...(toSettle.rows || []).flatMap((o) => o.owed.tickets.map((t) => t.photo_path)),
      ]),
    );
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const settle = async (order) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("create_traslado_settlement", { p_order: order.id });
    setBusy(false);
    if (err) setError(err.message);
    else load();
  };

  const markPaid = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("mark_traslado_settlement_paid", { p_settlement: paying.id, p_ref: ref });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setPaying(null);
    setRef("");
    load();
  };

  const line = (label, value, tone) => (
    <InlineStack align="space-between">
      <Text tone={tone} variant="bodySm">{label}</Text>
      <Text tone={tone} variant="bodySm">{value}</Text>
    </InlineStack>
  );

  // One settlement (existing or to create): header, total + actions, and the
  // expandable list of what it pays.
  const item = ({ id, orderId, order, name, badges, when, total, actions, chofer, second, secondName, comida, tickets, note }) => (
    <BlockStack key={id} gap="200">
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        <BlockStack gap="050">
          <InlineStack gap="200" blockAlign="center" align="start">
            <Link url={`/vehicle_transfers/${orderId}`}>{`Traslado #${orderId}`}</Link>
            <Text as="span" fontWeight="semibold">{name || "—"}</Text>
            {badges}
          </InlineStack>
          <Text tone="subdued" variant="bodySm">
            {[unit(order), order?.origin_label && `${order.origin_label} → ${order.dest_label || "?"}`, when].filter(Boolean).join(" · ")}
          </Text>
          <InlineStack>
            <Button variant="plain" disclosure={open[id] ? "up" : "down"} onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))}>
              {open[id] ? "Ocultar detalle" : "Ver qué incluye"}
            </Button>
          </InlineStack>
        </BlockStack>
        <BlockStack gap="100" inlineAlign="end">
          <Text variant="headingMd">{formatPrice(total)}</Text>
          {actions}
        </BlockStack>
      </InlineStack>
      <Collapsible open={!!open[id]} id={`liq-${id}`}>
        <div style={{ paddingLeft: 12, borderLeft: "2px solid var(--p-color-border)", maxWidth: 560 }}>
          <BlockStack gap="150">
            {Number(chofer) > 0 && line(Number(second) > 0 ? `Chofer · ${name || "principal"}` : "Chofer", formatPrice(chofer))}
            {Number(second) > 0 && line(`Chofer · ${secondName || "secundario"} (secundario, liquidación aparte)`, formatPrice(second))}
            {Number(comida) > 0 && line("Comida", formatPrice(comida))}
            {(tickets || []).map((t) => (
              <InlineStack key={t.id} gap="200" blockAlign="center" wrap={false}>
                <a href={urls[t.photo_path]} target="_blank" rel="noreferrer">
                  <img src={urls[t.photo_path]} alt="ticket" style={{ ...thumb, width: 32, height: 32 }} />
                </a>
                <Text variant="bodySm">{[KIND_LABEL[t.kind] || t.kind, t.merchant_name, t.ticket_date].filter(Boolean).join(" · ")}</Text>
                {t.deductible === false && <Badge tone="warning">No deducible</Badge>}
                <div style={{ marginLeft: "auto" }}>
                  <Text variant="bodySm">{formatPrice(t.total)}</Text>
                </div>
              </InlineStack>
            ))}
            {note}
          </BlockStack>
        </div>
      </Collapsible>
    </BlockStack>
  );

  return (
    <BlockStack gap="300">
      {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
      {owed.length > 0 && <Text variant="headingSm" tone="subdued">Por liquidar</Text>}
      {owed.map((o, i) => (
        <BlockStack key={`o${o.id}`} gap="200">
          {i > 0 && <Divider />}
          {item({
            id: `o${o.id}`, orderId: o.id, order: o, name: o.trasladista?.name || "Sin trasladista asignado",
            badges: o.owed.first ? <Badge tone="attention">Anticipo sin pagar</Badge> : null,
            total: o.owed.total, chofer: o.owed.chofer, second: o.owed.second, secondName: o.secondary?.name,
            comida: o.owed.comida, tickets: o.owed.tickets,
            actions: canPay && (
              <Button size="slim" variant="primary" loading={busy} disabled={!o.trasladista} onClick={() => settle(o)}>
                Liquidar
              </Button>
            ),
          })}
        </BlockStack>
      ))}
      {owed.length > 0 && rows.length > 0 && <Divider borderWidth="050" />}
      {rows.length > 0 && <Text variant="headingSm" tone="subdued">Por pagar</Text>}
      {rows.map((s, i) => (
        <BlockStack key={s.id} gap="200">
          {i > 0 && <Divider />}
          {item({
            id: s.id, orderId: s.traslado_order, order: s.order, name: s.trasladista?.name,
            badges: (
              <>
                {s.extra_reason ? (
                  <Badge tone="info">Pago adicional</Badge>
                ) : (
                  Number(s.amount_chofer) > 0 && <Badge>Anticipo</Badge>
                )}
                {s.returned_at && <Badge tone="critical">SPEI devuelto</Badge>}
              </>
            ),
            when: formatFriendlyDate(s.created_at), total: s.total,
            chofer: s.amount_chofer, comida: s.amount_comida, tickets: s.tickets,
            note: s.returned_at ? (
              <Text tone="critical" variant="bodySm">
                {`SPEI devuelto (${s.returned_reason || "sin motivo"}). Revisa la cuenta del trasladista antes de reintentar.`}
              </Text>
            ) : (
              s.extra_reason && (
                <Text tone="subdued" variant="bodySm">
                  {s.extra_reason}
                </Text>
              )
            ),
            actions: canPay && (
              <InlineStack gap="200" wrap={false}>
                {speiEnabled() && (
                  <Button size="slim" variant="primary" onClick={() => setSpeiFor(s)}>
                    Pagar por SPEI
                  </Button>
                )}
                <Button size="slim" variant="plain" onClick={() => setPaying(s)}>
                  Marcar pagado
                </Button>
              </InlineStack>
            ),
          })}
        </BlockStack>
      ))}

      {speiFor && (
        <SpeiPaymentModal
          open
          payableKind="traslado_settlement"
          payableId={speiFor.id}
          nounLabel="liquidación"
          beneficiaryLabel="trasladista"
          onClose={() => {
            setSpeiFor(null);
            load();
          }}
          onAuthorized={() => load()}
        />
      )}
      <Modal
        open={!!paying}
        onClose={() => setPaying(null)}
        title={paying ? `Marcar pagado ${formatPrice(paying.total)}` : ""}
        primaryAction={{ content: "Marcar pagado", loading: busy, disabled: ref.trim().length < 4, onAction: markPaid }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setPaying(null) }]}
      >
        <Modal.Section>
          <TextField label="Referencia o clave de rastreo del SPEI" autoComplete="off" value={ref} onChange={setRef} />
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}

function TrasladosPendientes() {
  const { hasPermission } = UserAuth();
  const canEdit = hasPermission("services.create");
  const canPay = hasPermission("services.pay");
  // null = still loading, -1 = its query failed (the card shows the error).
  const [counts, setCounts] = useState({ facturar: null, cfdis: null, aprobar: null, liquidar: null });
  const [settleKey, setSettleKey] = useState(0);
  const setCount = (k) => (n) => setCounts((c) => ({ ...c, [k]: n }));
  const show = (k) => ({ display: counts[k] ? undefined : "none" });
  const allLoaded = Object.values(counts).every((n) => n !== null);
  const nothing = allLoaded && Object.values(counts).every((n) => n === 0);

  const header = (title, n) => (
    <InlineStack gap="200" blockAlign="center">
      <Text variant="headingMd">{title}</Text>
      {n < 0 ? <Badge tone="critical">Error</Badge> : <Badge tone="attention">{String(n)}</Badge>}
    </InlineStack>
  );

  return (
    <AppWrapper>
      <Page
        title="Pendientes de traslados"
        subtitle="Facturar tickets, aprobar gastos y pagar liquidaciones de todos los traslados"
        backAction={{ url: "/vehicle_transfers" }}
      >
        <BlockStack gap="400">
          {nothing && (
            <Card>
              <Text tone="subdued">Todo al día: no hay tickets por facturar, facturas por revisar, gastos por aprobar ni liquidaciones pendientes.</Text>
            </Card>
          )}
          <div style={show("facturar")}>
            <Card padding="0">
              <div style={{ padding: "16px 16px 0" }}>{header("Tickets por facturar", counts.facturar)}</div>
              <PorFacturar canEdit={canEdit} onCount={setCount("facturar")} />
            </Card>
          </div>
          <div style={show("cfdis")}>
            <Card>
              <BlockStack gap="300">
                {header("Facturas recibidas sin ticket", counts.cfdis)}
                <FacturasPorRevisar onCount={setCount("cfdis")} />
              </BlockStack>
            </Card>
          </div>
          <div style={show("aprobar")}>
            <Card>
              <BlockStack gap="300">
                {header("Gastos por aprobar", counts.aprobar)}
                <PorAprobar canReview={canPay} onCount={setCount("aprobar")} onChange={() => setSettleKey((k) => k + 1)} />
              </BlockStack>
            </Card>
          </div>
          <div style={show("liquidar")}>
            <Card>
              <BlockStack gap="300">
                {header("Liquidaciones", counts.liquidar)}
                <LiquidacionesPendientes canPay={canPay} onCount={setCount("liquidar")} reloadKey={settleKey} />
              </BlockStack>
            </Card>
          </div>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default TrasladosPendientes;
